import { useEffect, useMemo, useState } from 'react'
import { Constellation, type ConstellationEdge, type ConstellationNode, type ConstellationTheme } from '@diwata/ironvale'
import { STARCHART, STARCHART_LIGHT } from '@/lib/graphColors'
import { useColorMode } from '@/lib/useColorMode'

/**
 * The Observatory hero sky — the ironvale Constellation in its ART-DIRECTED mode,
 * staged as the Direction B star chart: four region-labelled domain clusters in
 * the four separated hues, a lit and a dim tier, threads readable at rest, one
 * crimson-ringed live signal, and the ego unmistakably brightest at the centre.
 *
 * Every star is PLACED (stagePositions), the camera is locked, the primitive's
 * instrument chrome (hop rings, sector rays, auto-labels) is silenced, and the
 * mockup's decor — orbit arcs, region names, hued labels, the ego's double halo,
 * the live thread, and the hand-off thread descending toward the chip — is drawn
 * through the component's `overlay` projection so it rides the same camera.
 *
 * NO masks, NO scrims: composition separates the sky from the copy.
 */

// ── The composition (centre offsets; the ego holds (0,0)) ────────────────────

interface StagedStar {
  id: string
  label?: string
  domain: keyof typeof STARCHART
  x: number
  y: number
  tier: 'lit' | 'dim'
  /** Label rides above (default) or below the star. */
  labelBelow?: boolean
}

const STARS: StagedStar[] = [
  // NETWORK · dusk violet (upper left)
  { id: 'n-priya', label: 'PRIYA OKAFOR', domain: 'network', x: -342, y: -125, tier: 'dim' },
  { id: 'n-meridian', label: 'MERIDIAN CAPITAL', domain: 'network', x: -182, y: -55, tier: 'lit' },
  { id: 'n-far', domain: 'network', x: -437, y: 0, tier: 'dim' },
  // MARKETS · sea-glass teal (upper right)
  { id: 'm-argus', label: 'ARGUS DYNAMICS', domain: 'markets', x: 163, y: -85, tier: 'lit' },
  { id: 'm-note', label: 'SECTOR NOTE · PRIMES', domain: 'markets', x: 333, y: -115, tier: 'dim' },
  // MEDIA · ember copper (right flank)
  { id: 'd-cnbc', label: 'CNBC · DEFENSE TECH', domain: 'media', x: 438, y: 15, tier: 'dim' },
  { id: 'd-far', domain: 'media', x: 248, y: 70, tier: 'dim' },
  // GOVCON · burnished gold (lower left) — the live signal wears the crimson ring
  { id: 'g-signal', domain: 'govcon', x: -252, y: 50, tier: 'lit' },
  { id: 'g-far', domain: 'govcon', x: -372, y: 105, tier: 'dim' },
]

const EDGES: ConstellationEdge[] = [
  { from: 'n-priya', to: 'n-meridian' },
  { from: 'n-priya', to: 'n-far' },
  { from: 'n-meridian', to: 'ego' },
  { from: 'm-argus', to: 'm-note' },
  { from: 'm-argus', to: 'ego' },
  { from: 'm-note', to: 'd-cnbc' },
  { from: 'd-cnbc', to: 'ego' },
  { from: 'n-far', to: 'ego' },
  { from: 'g-signal', to: 'ego' },
  { from: 'g-signal', to: 'g-far' },
  { from: 'd-far', to: 'ego' },
]

const STAGE = Object.fromEntries(STARS.map((s) => [s.id, { x: s.x, y: s.y }]))
const LIVE_SIGNAL = STARS.find((s) => s.id === 'g-signal')!

// The dark sky: parchment/gold instrument grammar, crimson ego.
const DARK_THEME: ConstellationTheme = {
  ring: '#c9a96e',
  star: '#e8e2d9',
  starHalo: '#7a7268',
  focusCore: '#e0584a',
  focusHalo: '#f48a7f',
  label: '#e8e2d9',
  sector: '#7a7268',
  muted: '#7a7268',
}

// The parchment sky: deep-bronze threads, a crimson that holds on paper.
const LIGHT_THEME: ConstellationTheme = {
  ring: '#6b501c',
  star: '#241f18',
  starHalo: '#6a6153',
  focusCore: '#a62f24',
  focusHalo: '#c65043',
  label: '#241f18',
  sector: '#6a6153',
  muted: '#6a6153',
}

const CRIMSON = { dark: '#e0584a', light: '#a62f24' }

export function HeroConstellation() {
  const mode = useColorMode()
  const [reduced, setReduced] = useState(false)
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280))

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // the composition is authored at ~1240px — scale it down with the viewport
  const zoom = Math.min(1, Math.max(0.42, vw / 1240))

  const hues = mode === 'light' ? STARCHART_LIGHT : STARCHART
  const crimson = CRIMSON[mode]

  const nodes = useMemo<ConstellationNode[]>(
    () => [
      { id: 'ego', label: 'EGO', group: 'network', weight: 8, brightness: 1 },
      ...STARS.map((s) => ({
        id: s.id,
        label: s.label ?? '',
        group: s.domain,
        color: hues[s.domain],
        weight: s.tier === 'lit' ? 6.5 : 4,
        brightness: s.tier === 'lit' ? 0.85 : 0.28,
      })),
    ],
    [hues],
  )

  const theme = mode === 'light' ? LIGHT_THEME : DARK_THEME

  return (
    <div className="dwl-hero-sky" aria-hidden="true">
      <div className="dwl-hero-sky-inner">
        <Constellation
          nodes={nodes}
          edges={EDGES}
          egoId="ego"
          maxHop={3}
          reach={3}
          theme={theme}
          reducedMotion={reduced}
          timeControl={false}
          stagePositions={STAGE}
          lockCamera
          initialZoom={zoom}
          labelIds={[]}
          hopRings={false}
          sectorRays={false}
          overlay={(project, k) => {
            const o = project(0, 0)
            return (
              <svg>
                <g transform={`translate(${o.x} ${o.y}) scale(${k})`}>
                  {/* the astrolabe arcs — the sky's instrument line, upper sweep only */}
                  <path className="dwl-sky-orbit" d="M -622 80 A 620 300 0 0 1 618 80" />
                  <path className="dwl-sky-orbit" d="M -452 60 A 450 215 0 0 1 448 60" style={{ opacity: 0.1 }} />

                  {/* region names — the constellations of this sky */}
                  <text className="dwl-sky-region" x={-542} y={-62} fill={hues.network}>Network</text>
                  <text className="dwl-sky-region" x={455} y={-132} fill={hues.markets}>Markets</text>

                  {/* the live crimson thread over the gold one, signal → ego */}
                  <line className="dwl-sky-live-thread" x1={LIVE_SIGNAL.x} y1={LIVE_SIGNAL.y} x2={0} y2={0} />
                  {/* the hand-off: one live thread descends from the ego toward the chip */}
                  <line className="dwl-sky-live-thread" x1={0} y1={0} x2={0} y2={92} style={{ opacity: 0.45 }} />

                  {/* hued labels — lit tier bright, dim tier receding */}
                  {STARS.filter((s) => s.label).map((s) => (
                    <text
                      key={s.id}
                      className={s.tier === 'lit' ? 'dwl-sky-lbl' : 'dwl-sky-lbl dwl-sky-lbl--dim'}
                      x={s.x}
                      y={s.y + (s.labelBelow ? 24 : -17)}
                      fill={hues[s.domain]}
                      textAnchor="middle"
                    >
                      {s.label}
                    </text>
                  ))}

                  {/* the live signal — crimson ring + crimson label */}
                  <circle className="dwl-sky-signal-ring" cx={LIVE_SIGNAL.x} cy={LIVE_SIGNAL.y} r={11.5} />
                  <text
                    className="dwl-sky-lbl"
                    x={LIVE_SIGNAL.x - 18}
                    y={LIVE_SIGNAL.y - 20}
                    fill={crimson}
                    textAnchor="middle"
                  >
                    SDVOSB SET-ASIDE · $1.5M
                  </text>

                  {/* THE EGO — double halo + label; the brightest object in the sky */}
                  <circle className="dwl-sky-ego-ring dwl-sky-ego-halo" cx={0} cy={0} r={24} strokeWidth={0.8} style={{ opacity: 'var(--dwl-ego-halo)' as unknown as number }} />
                  <circle className="dwl-sky-ego-ring" cx={0} cy={0} r={15} strokeWidth={1} style={{ opacity: 'var(--dwl-ego-ring)' as unknown as number }} />
                  <text className="dwl-sky-lbl" x={34} y={4} fill={crimson} style={{ letterSpacing: '0.3em' }}>
                    EGO
                  </text>
                </g>
              </svg>
            )
          }}
        />
      </div>
    </div>
  )
}
